/**
 * Renders the toasts raised through `utils/toast`. Mounted once, in the shell.
 *
 * Errors use `role="alert"` so a screen reader announces them at once; the
 * rest are `role="status"` and wait their turn.
 */

import { CircleAlert, CircleCheck, Info, TriangleAlert, X, type LucideIcon } from 'lucide-react'
import { useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'

import { dismissToast, getToasts, subscribe, type Toast, type ToastTone } from '../../utils/toast'
import { cn, ICON_BUTTON } from './styles'

const TONE: Record<ToastTone, { icon: LucideIcon; ring: string; iconClass: string }> = {
    success: { icon: CircleCheck, ring: 'border-l-success', iconClass: 'text-success' },
    error: { icon: CircleAlert, ring: 'border-l-danger', iconClass: 'text-danger' },
    warning: { icon: TriangleAlert, ring: 'border-l-warning', iconClass: 'text-warning' },
    info: { icon: Info, ring: 'border-l-info', iconClass: 'text-info' },
}

export default function Toaster() {
    const toasts = useSyncExternalStore(subscribe, getToasts, getToasts)
    return createPortal(
        // The region exists before the first toast, so a screen reader announces each one as it arrives.
        <div
            aria-live="polite"
            className="pointer-events-none fixed bottom-4 right-4 z-90 flex w-96 max-w-[calc(100vw-2rem)] flex-col gap-2"
        >
            {toasts.map((item) => (
                <ToastCard key={item.id} toast={item} />
            ))}
        </div>,
        document.body,
    )
}

function ToastCard({ toast }: { toast: Toast }) {
    const tone = TONE[toast.tone]
    const Icon = tone.icon
    return (
        <div
            role={toast.tone === 'error' ? 'alert' : undefined}
            className={`pointer-events-auto flex items-start gap-3 rounded-xl border border-l-4 border-line bg-surface p-3 pr-2 text-sm shadow-raised animate-fadeIn ${tone.ring}`}
        >
            <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${tone.iconClass}`} aria-hidden="true" />
            <div className="min-w-0 flex-1">
                {toast.title && <p className="font-semibold text-fg-strong">{toast.title}</p>}
                <p className="whitespace-pre-line wrap-break-word text-fg">{toast.message}</p>
            </div>
            <button
                type="button"
                onClick={() => dismissToast(toast.id)}
                aria-label="Meldung schließen"
                className={cn(ICON_BUTTON, 'h-7 w-7')}
            >
                <X className="h-4 w-4" aria-hidden="true" />
            </button>
        </div>
    )
}
